import { defineStore } from 'pinia';

export type AppMode = 'success' | 'error' | 'info' | 'warning' | '';

export type ThemeName = 'lightTheme' | 'darkTheme'

export interface AppState {
  loading: boolean;
  mode: AppMode;
  snackbar: boolean;
  notice: string;
  timeoutId?: ReturnType<typeof setTimeout>;
  theme: ThemeName;
}

const MODES = {
  SUCCESS: 'success' as AppMode,
  ERROR: 'error' as AppMode,
  INFO: 'info' as AppMode,
  WARNING: 'warning' as AppMode,
};

export const useAppStore = defineStore('app', {
  state: (): AppState => ({
    loading: true,
    mode: '',
    snackbar: false,
    notice: '',
    timeoutId: undefined,
    theme: (localStorage.getItem('theme') as ThemeName) || 'lightTheme',
  }),

  getters: {
    isSuccess: (state): boolean => state.mode === MODES.SUCCESS,
    isError: (state): boolean => state.mode === MODES.ERROR,
    isNoticeVisible: (state): boolean => state.snackbar && state.notice !== '',
  },

  actions: {
    toggleTheme() {
      const newTheme = this.theme === 'lightTheme'
        ? 'darkTheme'
        : 'lightTheme'
      this.setTheme(newTheme);
    },

    setTheme(theme: ThemeName) {
      this.theme = theme;
      localStorage.setItem('theme', theme);
    },

    setLoading(loading: boolean) {
      this.loading = loading;
    },
    
    setNotice(notice: string) {
      this.notice = notice;
    },
    
    setSnackbar(snackbar: boolean) {
      this.snackbar = snackbar;
    },
    
    setMode(mode: AppMode) {
      this.mode = mode;
    },

    clearTimeout() {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = undefined;
      }
    },
    
    closeNotice() {
      this.clearTimeout();
      this.setNotice('');
      this.setMode('');
      this.setSnackbar(false);
    },

    showNotice(
      notice: string,
      mode: AppMode,
      autoClose = true,
      timeout = 5000
    ) {
      this.clearTimeout();
      this.notice = notice;
      this.mode = mode;
      this.snackbar = true;

      if (autoClose) {
        this.timeoutId = setTimeout(() => {
          this.closeNotice();
        }, timeout);
      }
    },
    
    closeNoticeWithDelay(timeout = 5000) {
      setTimeout(() => {
        this.closeNotice();
      }, timeout);
    },
    
    sendSuccessNotice(notice: string) {
       this.showNotice(notice, MODES.SUCCESS);
    },
    
    sendErrorNotice(notice: string) {
       this.showNotice(notice, MODES.ERROR, false);
    },
    
    sendInfoNotice(notice: string, autoClose = true) {
       this.showNotice(notice, MODES.INFO, autoClose);
    },
    
    sendWarningNotice(notice: string, autoClose = true) {
      this.showNotice(notice, MODES.WARNING, autoClose);
    },
    
    // Async example if needed
    async fetchAppConfig() {
      this.setLoading(true);
      try {
        // Your async logic here
        // const config = await api.getConfig();
        // this.setConfig(config);
      } catch (error) {
        this.sendErrorNotice('Failed to load configuration');
      } finally {
        this.setLoading(false);
      }
    },
  },
});